import '../../styles/restScreens.css';
import { useEffect, useState } from 'react';
import { supabaseClient } from '../../config/supabase';

export const LOGO_BUCKET = 'entity-logos';
export const LOGO_MAX_BYTES = 1024 * 1024;
const LOGO_TYPES = { 'image/png': 'png', 'image/jpeg': 'jpg', 'image/svg+xml': 'svg' };
const EXTENSION_TYPES = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', svg: 'image/svg+xml' };
const PUBLIC_MARKER = `/storage/v1/object/public/${LOGO_BUCKET}/`;

const readText = (file) => (typeof file.text === 'function'
    ? file.text()
    : new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result || ''));
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
    }));

// Emails and the app render logos as images, but an SVG opened directly could still run script.
const UNSAFE_SVG = /<script|<foreignobject|\son[a-z]+\s*=|javascript:|<iframe|<embed|<object/i;

export async function validateLogoFile(file) {
    if (!file) return { error: 'Choose a logo file.' };
    const extension = String(file.name || '').split('.').pop().toLowerCase();
    const type = LOGO_TYPES[file.type] ? file.type : EXTENSION_TYPES[extension];
    if (!type) return { error: 'Logos must be PNG, JPG, or SVG.' };
    if (file.size > LOGO_MAX_BYTES) return { error: 'Logos must be 1 MB or smaller.' };
    if (type === 'image/svg+xml' && UNSAFE_SVG.test(await readText(file))) {
        return { error: 'This SVG contains scripts or links and cannot be used as a logo.' };
    }
    return { type, extension: LOGO_TYPES[type] };
}

export const logoObjectPath = (url) => {
    const index = typeof url === 'string' ? url.indexOf(PUBLIC_MARKER) : -1;
    return index < 0 ? null : decodeURIComponent(url.slice(index + PUBLIC_MARKER.length).split('?')[0]);
};

function LogoRow({ entity, canEdit, onChanged }) {
    const [brandName, setBrandName] = useState(entity.brand_name || '');
    const [pending, setPending] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const displayName = entity.brand_name || entity.name;
    const kind = entity.level_type === 'organization' ? 'Organization' : 'Company';

    useEffect(() => { setBrandName(entity.brand_name || ''); }, [entity.brand_name]);
    useEffect(() => () => { if (pending?.preview) URL.revokeObjectURL(pending.preview); }, [pending]);

    const run = async (work, success) => {
        setBusy(true); setError(''); setNotice('');
        try {
            await work();
            setNotice(success);
        } catch (failure) {
            setError(failure?.message || 'Could not save. Try again.');
        } finally {
            setBusy(false);
        }
    };
    const saveEntity = async (changes) => {
        const { data, error: saveError } = await supabaseClient.from('entities')
            .update(changes).eq('id', entity.id).select().single();
        if (saveError) throw saveError;
        await onChanged(data);
    };
    const choose = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        setError(''); setNotice('');
        if (!file) return;
        const checked = await validateLogoFile(file);
        if (checked.error) { setError(checked.error); return; }
        setPending({ file, ...checked, preview: URL.createObjectURL(file) });
    };
    const upload = () => run(async () => {
        const path = `${entity.org_id}/${entity.id}/logo-${Date.now()}.${pending.extension}`;
        const bucket = supabaseClient.storage.from(LOGO_BUCKET);
        const { error: uploadError } = await bucket.upload(path, pending.file, {
            contentType: pending.type, cacheControl: '3600', upsert: false
        });
        if (uploadError) throw uploadError;
        const { data } = bucket.getPublicUrl(path);
        try {
            await saveEntity({ logo: data.publicUrl });
        } catch (saveError) {
            await bucket.remove([path]);
            throw saveError;
        }
        const previous = logoObjectPath(entity.logo);
        if (previous && previous !== path) await bucket.remove([previous]);
        setPending(null);
    }, 'Logo saved.');
    const remove = () => {
        if (!window.confirm(`Remove the ${displayName} logo? Emails will fall back to the next logo up, then the name.`)) return;
        run(async () => {
            const previous = logoObjectPath(entity.logo);
            await saveEntity({ logo: null });
            if (previous) await supabaseClient.storage.from(LOGO_BUCKET).remove([previous]);
        }, 'Logo removed.');
    };
    const saveBrand = () => run(() => saveEntity({ brand_name: brandName.trim() || null }), 'Brand name saved.');
    const preview = pending?.preview || entity.logo;
    const inputId = `logo-file-${entity.id}`;

    return (
        <div className="rounded-lg border border-[var(--surface-border)] p-4" data-testid={`logo-row-${entity.id}`}>
            <div className="flex flex-wrap items-start gap-4">
                <div className="flex h-20 w-40 items-center justify-center rounded border border-dashed border-[var(--surface-border)] bg-white p-2">
                    {preview
                        ? <img src={preview} alt={`${displayName} logo`} className="max-h-16 max-w-[144px] object-contain" />
                        : <span className="text-center text-sm font-bold text-gray-700">{displayName}</span>}
                </div>
                <div className="min-w-[220px] flex-1 space-y-3">
                    <div className="flex items-center gap-2">
                        <span className="font-semibold text-[var(--ink)]">{entity.name}</span>
                        <span className="rounded bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{kind}</span>
                    </div>
                    <label className="block text-sm">
                        <span className="mb-1 block text-[var(--ink-muted,inherit)]">Brand name (shown in emails)</span>
                        <div className="flex gap-2">
                            <input value={brandName} maxLength={120} disabled={!canEdit || busy}
                                placeholder={entity.name} aria-label={`${entity.name} brand name`}
                                onChange={(event) => setBrandName(event.target.value)}
                                className="flex-1 rounded border border-[var(--surface-border)] bg-transparent px-3 py-1.5" />
                            {canEdit && <button type="button" disabled={busy || brandName.trim() === (entity.brand_name || '')}
                                onClick={saveBrand} className="rounded border border-[var(--surface-border)] px-3 py-1.5 text-sm disabled:opacity-50">Save name</button>}
                        </div>
                    </label>
                    {canEdit && <div className="flex flex-wrap items-center gap-2 text-sm">
                        <label htmlFor={inputId} className={`cursor-pointer rounded border border-[var(--surface-border)] px-3 py-1.5 ${busy ? 'pointer-events-none opacity-50' : ''}`}>
                            {entity.logo ? 'Replace logo' : 'Upload logo'}
                        </label>
                        <input id={inputId} type="file" className="sr-only" disabled={busy}
                            aria-label={`${entity.name} logo file`}
                            accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml" onChange={choose} />
                        {pending && <>
                            <button type="button" disabled={busy} onClick={upload} className="rounded bg-brand-600 px-3 py-1.5 text-white disabled:opacity-50">{busy ? 'Saving…' : 'Save logo'}</button>
                            <button type="button" disabled={busy} onClick={() => setPending(null)} className="rounded px-3 py-1.5">Cancel</button>
                        </>}
                        {!pending && entity.logo && <button type="button" disabled={busy} onClick={remove} className="rounded px-3 py-1.5 text-red-600 hover:bg-red-50 disabled:opacity-50">Remove logo</button>}
                        <span className="text-xs text-gray-500">PNG, JPG, or SVG · 1 MB max</span>
                    </div>}
                    {error && <p role="alert" className="text-sm text-red-600">{error}</p>}
                    {notice && <p role="status" className="text-sm text-[var(--selected-pill-ink)]">{notice}</p>}
                </div>
            </div>
        </div>
    );
}

// Invite, code, and reset emails use the Company logo, then the Organization logo, then the name.
export default function CompanyLogoPanel({ entities, onChanged }) {
    const [isAdmin, setIsAdmin] = useState(null);
    useEffect(() => {
        let active = true;
        supabaseClient.rpc('is_org_admin').then(({ data, error }) => {
            if (active) setIsAdmin(!error && data === true);
        }, () => { if (active) setIsAdmin(false); });
        return () => { active = false; };
    }, []);
    const branded = (entities || []).filter((entity) => ['organization', 'company'].includes(entity.level_type)
        && entity.status !== 'archived');
    if (branded.length === 0) return <p data-m6-layout="entities" data-m6-component="CompanyLogoPanel" className="text-sm text-gray-500">Create a Company first.</p>;
    return (
        <div data-m6-layout="entities" data-m6-component="CompanyLogoPanel" className="space-y-3">
            {isAdmin === false && <p className="text-sm text-gray-500">Only an organization owner or admin can change logos and brand names.</p>}
            {branded.map((entity) => <LogoRow key={entity.id} entity={entity} canEdit={isAdmin === true} onChanged={onChanged} />)}
        </div>
    );
}
